<script setup>
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import RadioButton from "primevue/radiobutton";
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import Textarea from "primevue/textarea";
import InputNumber from "primevue/inputnumber";
import {ref} from "vue";
import { trans } from 'laravel-vue-i18n';

const model = defineModel();
const trueRef = ref(true);

const formTypes = [
    {
        type: 'checkbox',
        label: trans('forms.builder_type_checkbox'),
        icon: 'pi pi-check-square'
    },
    {
        type: 'radio',
        label: trans('forms.builder_type_radio'),
        icon: 'pi pi-circle'
    },
    {
        type: 'text',
        label: 'Text',
        icon: 'pi pi-pen-to-square'
    },
    {
        type: 'number',
        label: trans('forms.builder_type_number'),
        icon: 'pi pi-hashtag'
    },
    {
        type: 'textarea',
        label: trans('forms.builder_type_textarea'),
        icon: 'pi pi-align-left'
    },
    {
        type: 'list',
        label: trans('forms.builder_type_list'),
        icon: 'pi pi-arrow-circle-down'
    }
];

const add = (type) => {
    switch (type) {
        case 'text':
        case 'textarea':
        case 'number':
        case 'checkbox':
        case 'radio':
        case 'list':
            model.value.push({
                type: type,
                required: false,
                label: '',
                options: [
                    {
                        label: ''
                    }
                ]
            })
            break;
    }

    activeCard.value = model.value.length - 1;
}

const activeCard = ref(0);
</script>

<template>
    <div class="border rounded-xl p-4 bg-gray-50 dark:bg-gray-900 dark:border-neutral-700 flex flex-col gap-4 shadow-md">
        <div v-for="(input, i) in model" class="border bg-white dark:bg-black dark:border-neutral-700 relative rounded-lg shadow flex flex-col gap-2"
             :key="i" @click="activeCard = i" :class="activeCard === i ? 'p-3' : 'p-4'">
            <div class="flex gap-2">
                <template v-if="activeCard === i">
                    <p v-if="input.required"
                       class="text-red-500 absolute top-3 right-2.5 bg-red-200 flex items-center justify-center rounded-full"
                       style="width:16px; height: 16px;">
                        <i class=" pi pi-asterisk" style="font-size: 12px"/>
                    </p>
                    <InputText unstyled :placeholder="$t('forms.builder_enter_label')" v-model="input.label" :class="!!$page.props.errors[`formBuilder.${i}.label`] ? 'border-red-600' : ''"
                               class="bg-transparent outline-none font-semibold mb-2 grow border-b border-transparent hover:border-gray-200 dark:hover:border-gray-700 transition-all"></InputText>
                </template>
                <template v-else>
                    <p class="font-semibold">
                        <span v-if="input.label !== ''">{{ input.label }}</span>
                        <span v-else class="italic opacity-70">{{ $t('forms.builder_enter_label_required') }}</span>
                        <span v-if="input.required" class="text-red-500 ml-1">*</span>
                    </p>
                </template>
            </div>
            <small v-if="$page.props.errors[`formBuilder.${i}.label`]" class="text-red-500 -mt-4">{{$page.props.errors[`formBuilder.${i}.label`]}}</small>
            <template v-if="input.type === 'checkbox' || input.type === 'radio' || input.type === 'list'">
                <div v-for="(child, y) in input.options" class="flex items-center gap-2">
                    <Checkbox v-if="input.type === 'checkbox'"></Checkbox>
                    <RadioButton v-if="input.type === 'radio'"></RadioButton>
                    <p v-if="input.type === 'list'">{{ y + 1 }}.</p>
                    <InputText unstyled :placeholder="$t('forms.builder_enter_option_name')" v-model="child.label"
                               class="bg-transparent outline-none" :class="activeCard === i ? ' border-b dark:border-gray-700':''"></InputText>

                    <template v-if="activeCard === i">
                        <Button size="small" class="-ml-2" severity="secondary" v-if="y === input.options.length - 1"
                                v-tooltip="child.label === '' ? $t('forms.builder_must_enter_name') : ''" :disabled="child.label===''"
                                @click="input.options.push({ label: '' })" text icon="pi pi-plus"></Button>
                        <Button size="small" class="-ml-2" severity="secondary"  :class="y !== input.options.length - 1 ? 'ml-6' : ''"
                                v-if="y !== 0 || input.options.length > 1" @click="input.options.splice(y, 1)" text
                                icon="pi pi-times"></Button>
                    </template>
                    <small v-if="$page.props.errors[`formBuilder.${i}.options.${y}.label`]" class="text-red-500 ">{{$page.props.errors[`formBuilder.${i}.options.${y}.label`]}}</small>
                </div>
            </template>

            <div class="flex items-center gap-2" v-if="input.type === 'text'">
                <InputText size="small" :placeholder="$t('forms.builder_text_placeholder')"></InputText>
            </div>

            <div class="flex items-center gap-2" v-if="input.type === 'number'">
                <InputNumber size="small" :placeholder="$t('forms.builder_number_placeholder')" showButtons buttonLayout="horizontal">
                    <template #incrementbuttonicon>
                        <span class="pi pi-plus"/>
                    </template>
                    <template #decrementbuttonicon>
                        <span class="pi pi-minus"/>
                    </template>
                </InputNumber>
            </div>

            <div class="flex items-center gap-2" v-if="input.type === 'textarea'">
                <Textarea size="small" :placeholder="$t('forms.builder_text_placeholder')"></Textarea>
            </div>


            <div v-if="activeCard === i" class="border-t dark:border-neutral-700 flex gap-2 items-center -mx-3 px-3 pt-3 mt-3">
                <Select v-model="input.type" :options="formTypes" size="small" class="max-w-[220px]" option-value="type">
                    <template #value="{ value }">
                        <div class="flex items-center"><span :class="formTypes.find(el => el.type === value).icon"
                                                             class="text-roori-blue mr-2"></span>
                            <span>{{ formTypes.find(el => el.type === value).label }}</span></div>
                    </template>
                    <template #option="{option}">
                        <span :class="option.icon" class="text-roori-blue mr-2"></span> {{ option.label }}
                    </template>
                </Select>
                <div class="grow"></div>
                <Button icon="pi pi-asterisk" size="small" :label="$t('forms.builder_required')" @click="input.required = !input.required"
                        :variant="!input.required ? 'outlined' : null" severity="secondary"></Button>
                <Button icon="pi pi-trash" @click="model.splice(i, 1)" size="small" severity="danger"></Button>
            </div>
        </div>
        <div class="border rounded-lg border-dashed border-gray-400 dark:border-neutral-700 p-4">
            <div class="flex flex-wrap justify-center gap-2">
                <Button v-for="input in formTypes" :key="input.label" :icon="input.icon" :label="input.label"
                        @click="add(input.type)" variant="outlined" severity="secondary" size="small"></Button>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">

</style>
